import React, { useState, useEffect } from 'react';
import { 
  TrendingUp, 
  TrendingDown, 
  DollarSign, 
  Calendar, 
  FileText, 
  Download,
  RefreshCw,
  PieChart,
  BarChart3,
  Wallet,
  ArrowUpRight,
  ArrowDownRight
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { reportsApi } from '../lib/api';
import { formatCurrency, formatDate } from '../lib/utils';
import { toast } from 'sonner';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';

export const FinancialReports = ({ embedded = false }) => {
  const [activeTab, setActiveTab] = useState('pnl');
  const [loading, setLoading] = useState(false);
  
  // P&L State
  const [pnlData, setPnlData] = useState(null);
  const [pnlPeriod, setPnlPeriod] = useState('ytd');
  const [pnlYear, setPnlYear] = useState(new Date().getFullYear());
  const [pnlMonth, setPnlMonth] = useState(new Date().getMonth() + 1);
  const [pnlQuarter, setPnlQuarter] = useState(Math.ceil((new Date().getMonth() + 1) / 3));
  const [customStartDate, setCustomStartDate] = useState('');
  const [customEndDate, setCustomEndDate] = useState('');
  
  // YTD Summary State
  const [ytdData, setYtdData] = useState(null);
  const [ytdYear, setYtdYear] = useState(new Date().getFullYear());
  
  // Cash Flow State
  const [cashFlowData, setCashFlowData] = useState(null);
  const [cashFlowMonths, setCashFlowMonths] = useState(12);

  const currentYear = new Date().getFullYear();
  const years = Array.from({ length: 10 }, (_, i) => currentYear - i);
  const months = [
    'January', 'February', 'March', 'April', 'May', 'June',
    'July', 'August', 'September', 'October', 'November', 'December'
  ];

  useEffect(() => {
    if (activeTab === 'pnl') loadPnL();
    else if (activeTab === 'ytd') loadYtdSummary();
    else if (activeTab === 'cashflow') loadCashFlow();
  }, [activeTab]);

  const loadPnL = async () => {
    setLoading(true);
    try {
      const params = { period: pnlPeriod, year: pnlYear };
      if (pnlPeriod === 'monthly') params.month = pnlMonth;
      if (pnlPeriod === 'quarterly') params.quarter = pnlQuarter;
      if (pnlPeriod === 'custom') {
        params.start_date = customStartDate;
        params.end_date = customEndDate;
      }
      const response = await reportsApi.getPnL(params);
      setPnlData(response.data);
    } catch (error) {
      console.error('Failed to load P&L:', error);
      toast.error('Failed to load P&L report');
    } finally {
      setLoading(false);
    }
  };

  const loadYtdSummary = async () => {
    setLoading(true);
    try {
      const response = await reportsApi.getYtdSummary(ytdYear);
      setYtdData(response.data);
    } catch (error) {
      console.error('Failed to load YTD summary:', error);
      toast.error('Failed to load YTD summary');
    } finally {
      setLoading(false);
    }
  };

  const loadCashFlow = async () => {
    setLoading(true);
    try {
      const response = await reportsApi.getCashFlowProjection(cashFlowMonths);
      setCashFlowData(response.data);
    } catch (error) {
      console.error('Failed to load cash flow:', error);
      toast.error('Failed to load cash flow projection');
    } finally {
      setLoading(false);
    }
  };

  const exportPnLToPDF = () => {
    if (!pnlData) return;
    
    const doc = new jsPDF();
    doc.setFontSize(18);
    doc.text('Profit & Loss Report', 14, 20);
    doc.setFontSize(12);
    doc.text(pnlData.period_label, 14, 30);
    doc.text(`Generated: ${new Date().toLocaleDateString()}`, 14, 38);
    
    // Summary
    doc.setFontSize(14);
    doc.text('Summary', 14, 52);
    
    autoTable(doc, {
      startY: 58,
      head: [['Metric', 'Amount']],
      body: [
        ['Total Revenue', formatCurrency(pnlData.summary.total_revenue)],
        ['Total Expenses', formatCurrency(pnlData.summary.total_expenses)],
        ['Gross Profit', formatCurrency(pnlData.summary.gross_profit)],
        ['Net Profit', formatCurrency(pnlData.summary.net_profit)],
        ['Deals Closed', pnlData.summary.deals_closed.toString()],
      ],
      theme: 'striped',
      headStyles: { fillColor: [26, 47, 35] }
    });
    
    // Deals
    if (pnlData.projects?.length > 0) {
      doc.addPage();
      doc.setFontSize(14);
      doc.text('Deals Breakdown', 14, 20);
      
      autoTable(doc, {
        startY: 26,
        head: [['Project', 'Sale Date', 'Sale Price', 'Expenses', 'Gross Profit', 'Net Profit']],
        body: pnlData.projects.map(d => [
          d.name,
          d.sale_date,
          formatCurrency(d.sale_price),
          formatCurrency(d.expenses),
          formatCurrency(d.gross_profit),
          formatCurrency(d.net_profit)
        ]),
        theme: 'striped',
        headStyles: { fillColor: [26, 47, 35] }
      });
    }
    
    doc.save(`PnL_Report_${pnlData.period_label.replace(/\s/g, '_')}.pdf`);
    toast.success('PDF exported');
  };

  const exportCashFlowToPDF = () => {
    if (!cashFlowData) return;
    
    const doc = new jsPDF();
    doc.setFontSize(18);
    doc.text('Cash Flow Projection', 14, 20);
    doc.setFontSize(12);
    doc.text(`Next ${cashFlowMonths} months from notes receivable`, 14, 30);
    doc.text(`Generated: ${new Date().toLocaleDateString()}`, 14, 38);
    
    // Summary
    autoTable(doc, {
      startY: 48,
      head: [['Metric', 'Amount']],
      body: [
        ['Total Notes Receivable', formatCurrency(cashFlowData.summary.total_notes_receivable)],
        ['Total Expected Payments', formatCurrency(cashFlowData.summary.total_expected_payments)],
        ['Active Notes', cashFlowData.summary.active_notes.toString()],
      ],
      theme: 'striped',
      headStyles: { fillColor: [26, 47, 35] }
    });
    
    // Monthly projections
    if (cashFlowData.monthly_projections?.length > 0) {
      doc.addPage();
      doc.setFontSize(14);
      doc.text('Monthly Projections', 14, 20);
      
      autoTable(doc, {
        startY: 26,
        head: [['Month', 'Expected Payments', 'Notes Due']],
        body: cashFlowData.monthly_projections.map(m => [
          m.month,
          formatCurrency(m.expected_payments),
          m.notes_count.toString()
        ]),
        theme: 'striped',
        headStyles: { fillColor: [26, 47, 35] }
      });
    }
    
    doc.save(`CashFlow_Projection_${cashFlowMonths}mo.pdf`);
    toast.success('PDF exported');
  };

  return (
    <div className="space-y-6" data-testid="financial-reports-page">
      {!embedded && (
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-xl sm:text-2xl font-heading font-bold text-foreground">Financial Reports</h1>
            <p className="text-xs sm:text-sm text-muted-foreground">P&L, cash flow projections, and financial summaries</p>
          </div>
        </div>
      )}

      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="grid w-full grid-cols-3 rounded-sm">
          <TabsTrigger value="pnl" className="rounded-sm text-xs sm:text-sm" data-testid="tab-pnl">
            <BarChart3 className="w-4 h-4 sm:mr-2" /> <span className="hidden sm:inline">P&L Report</span><span className="sm:hidden">P&L</span>
          </TabsTrigger>
          <TabsTrigger value="ytd" className="rounded-sm text-xs sm:text-sm" data-testid="tab-ytd">
            <PieChart className="w-4 h-4 sm:mr-2" /> <span className="hidden sm:inline">YTD Summary</span><span className="sm:hidden">YTD</span>
          </TabsTrigger>
          <TabsTrigger value="cashflow" className="rounded-sm text-xs sm:text-sm" data-testid="tab-cashflow">
            <Wallet className="w-4 h-4 sm:mr-2" /> <span className="hidden sm:inline">Cash Flow</span><span className="sm:hidden">Cash</span>
          </TabsTrigger>
        </TabsList>

        {/* P&L Report Tab */}
        <TabsContent value="pnl" className="space-y-4">
          <Card className="rounded-sm border">
            <CardHeader className="pb-2">
              <div className="flex items-center justify-between flex-wrap gap-4">
                <div>
                  <CardTitle className="text-base">Profit & Loss Report</CardTitle>
                  <CardDescription>Revenue, expenses, and profitability analysis</CardDescription>
                </div>
                <div className="flex items-center gap-2 flex-wrap">
                  <Select value={pnlPeriod} onValueChange={setPnlPeriod}>
                    <SelectTrigger className="w-32 rounded-sm">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="ytd">Year to Date</SelectItem>
                      <SelectItem value="monthly">Monthly</SelectItem>
                      <SelectItem value="quarterly">Quarterly</SelectItem>
                      <SelectItem value="annual">Annual</SelectItem>
                      <SelectItem value="custom">Custom</SelectItem>
                    </SelectContent>
                  </Select>
                  
                  {pnlPeriod !== 'custom' && (
                    <Select value={pnlYear.toString()} onValueChange={(v) => setPnlYear(parseInt(v))}>
                      <SelectTrigger className="w-24 rounded-sm">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {years.map(y => <SelectItem key={y} value={y.toString()}>{y}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  )}
                  
                  {pnlPeriod === 'monthly' && (
                    <Select value={pnlMonth.toString()} onValueChange={(v) => setPnlMonth(parseInt(v))}>
                      <SelectTrigger className="w-32 rounded-sm">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {months.map((m, i) => <SelectItem key={i} value={(i + 1).toString()}>{m}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  )}
                  
                  {pnlPeriod === 'quarterly' && (
                    <Select value={pnlQuarter.toString()} onValueChange={(v) => setPnlQuarter(parseInt(v))}>
                      <SelectTrigger className="w-20 rounded-sm">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="1">Q1</SelectItem>
                        <SelectItem value="2">Q2</SelectItem>
                        <SelectItem value="3">Q3</SelectItem>
                        <SelectItem value="4">Q4</SelectItem>
                      </SelectContent>
                    </Select>
                  )}
                  
                  {pnlPeriod === 'custom' && (
                    <>
                      <Input
                        type="date"
                        value={customStartDate}
                        onChange={(e) => setCustomStartDate(e.target.value)}
                        className="w-36 rounded-sm"
                      />
                      <span className="text-muted-foreground">to</span>
                      <Input
                        type="date"
                        value={customEndDate}
                        onChange={(e) => setCustomEndDate(e.target.value)}
                        className="w-36 rounded-sm"
                      />
                    </>
                  )}
                  
                  <Button onClick={loadPnL} disabled={loading} size="sm" className="rounded-sm">
                    {loading ? <RefreshCw className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />}
                  </Button>
                  
                  {pnlData && (
                    <Button onClick={exportPnLToPDF} variant="outline" size="sm" className="rounded-sm">
                      <Download className="w-4 h-4 mr-1" /> Export PDF
                    </Button>
                  )}
                </div>
              </div>
            </CardHeader>
            <CardContent>
              {loading ? (
                <div className="py-12 text-center text-muted-foreground">Loading report...</div>
              ) : pnlData ? (
                <div className="space-y-6">
                  {/* Period Label */}
                  <div className="text-center">
                    <Badge variant="outline" className="text-lg px-4 py-1 rounded-sm">
                      {pnlData.period_label}
                    </Badge>
                  </div>
                  
                  {/* Summary Cards */}
                  <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
                    <Card className="rounded-sm border">
                      <CardContent className="p-4">
                        <p className="text-xs uppercase text-muted-foreground">Total Revenue</p>
                        <p className="text-xl font-bold font-mono text-emerald-600">
                          {formatCurrency(pnlData.summary.total_revenue)}
                        </p>
                      </CardContent>
                    </Card>
                    <Card className="rounded-sm border">
                      <CardContent className="p-4">
                        <p className="text-xs uppercase text-muted-foreground">Total Expenses</p>
                        <p className="text-xl font-bold font-mono text-red-600">
                          {formatCurrency(pnlData.summary.total_expenses)}
                        </p>
                      </CardContent>
                    </Card>
                    <Card className="rounded-sm border">
                      <CardContent className="p-4">
                        <p className="text-xs uppercase text-muted-foreground">Gross Profit</p>
                        <p className="text-xl font-bold font-mono">
                          {formatCurrency(pnlData.summary.gross_profit)}
                        </p>
                      </CardContent>
                    </Card>
                    <Card className="rounded-sm border">
                      <CardContent className="p-4">
                        <p className="text-xs uppercase text-muted-foreground">Net Profit</p>
                        <p className="text-xl font-bold font-mono text-emerald-600">
                          {formatCurrency(pnlData.summary.net_profit)}
                        </p>
                      </CardContent>
                    </Card>
                    <Card className="rounded-sm border">
                      <CardContent className="p-4">
                        <p className="text-xs uppercase text-muted-foreground">Deals Closed</p>
                        <p className="text-xl font-bold font-mono">
                          {pnlData.summary.deals_closed}
                        </p>
                      </CardContent>
                    </Card>
                  </div>
                  
                  {/* Deals Table */}
                  {pnlData.projects?.length > 0 && (
                    <div>
                      <h3 className="font-semibold mb-3">Deals Breakdown</h3>
                      <div className="overflow-x-auto">
                        <table className="w-full text-sm">
                          <thead>
                            <tr className="border-b text-left">
                              <th className="pb-2 font-medium text-muted-foreground">Project</th>
                              <th className="pb-2 font-medium text-muted-foreground">Sale Date</th>
                              <th className="pb-2 font-medium text-muted-foreground text-right">Sale Price</th>
                              <th className="pb-2 font-medium text-muted-foreground text-right">Expenses</th>
                              <th className="pb-2 font-medium text-muted-foreground text-right">Gross Profit</th>
                              <th className="pb-2 font-medium text-muted-foreground text-right">Margin</th>
                            </tr>
                          </thead>
                          <tbody>
                            {pnlData.projects.map((deal, idx) => (
                              <tr key={idx} className="border-b">
                                <td className="py-2 font-medium">{deal.name}</td>
                                <td className="py-2">{deal.sale_date}</td>
                                <td className="py-2 text-right font-mono text-emerald-600">
                                  {formatCurrency(deal.sale_price)}
                                </td>
                                <td className="py-2 text-right font-mono text-red-600">
                                  {formatCurrency(deal.expenses)}
                                </td>
                                <td className="py-2 text-right font-mono text-blue-600">
                                  {formatCurrency(deal.gross_profit)}
                                </td>
                                <td className="py-2 text-right font-mono">
                                  {deal.sale_price > 0 ? ((deal.gross_profit / deal.sale_price) * 100).toFixed(1) : 0}%
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  )}
                  
                  {pnlData.projects?.length === 0 && (
                    <div className="py-8 text-center text-muted-foreground">
                      No deals closed in this period.
                    </div>
                  )}
                </div>
              ) : (
                <div className="py-12 text-center text-muted-foreground">
                  Select a period and click refresh to load the P&L report.
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* YTD Summary Tab */}
        <TabsContent value="ytd" className="space-y-4">
          <Card className="rounded-sm border">
            <CardHeader className="pb-2">
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle className="text-base">Year-to-Date Owner Summary</CardTitle>
                  <CardDescription>Annual financial performance overview</CardDescription>
                </div>
                <div className="flex items-center gap-2">
                  <Select value={ytdYear.toString()} onValueChange={(v) => setYtdYear(parseInt(v))}>
                    <SelectTrigger className="w-24 rounded-sm">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {years.map(y => <SelectItem key={y} value={y.toString()}>{y}</SelectItem>)}
                    </SelectContent>
                  </Select>
                  <Button onClick={loadYtdSummary} disabled={loading} size="sm" className="rounded-sm">
                    {loading ? <RefreshCw className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />}
                  </Button>
                </div>
              </div>
            </CardHeader>
            <CardContent>
              {loading ? (
                <div className="py-12 text-center text-muted-foreground">Loading summary...</div>
              ) : ytdData ? (
                <div className="space-y-6">
                  <div className="text-center">
                    <Badge variant="outline" className="text-lg px-4 py-1 rounded-sm">
                      {ytdData.year} Year-to-Date Summary
                    </Badge>
                  </div>
                  
                  {/* Key Metrics */}
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                    <Card className="rounded-sm">
                      <CardContent className="p-4">
                        <p className="text-xs uppercase text-muted-foreground">Total Revenue</p>
                        <p className="text-2xl font-bold font-mono text-emerald-600">
                          {formatCurrency(ytdData.summary.total_revenue)}
                        </p>
                      </CardContent>
                    </Card>
                    <Card className="rounded-sm">
                      <CardContent className="p-4">
                        <p className="text-xs uppercase text-muted-foreground">Net Profit</p>
                        <p className="text-2xl font-bold font-mono text-forest-600">
                          {formatCurrency(ytdData.summary.net_profit)}
                        </p>
                      </CardContent>
                    </Card>
                    <Card className="rounded-sm">
                      <CardContent className="p-4">
                        <p className="text-xs uppercase text-muted-foreground">Deals Closed</p>
                        <p className="text-2xl font-bold font-mono">
                          {ytdData.summary.deals_closed}
                        </p>
                      </CardContent>
                    </Card>
                    <Card className="rounded-sm">
                      <CardContent className="p-4">
                        <p className="text-xs uppercase text-muted-foreground">Avg Profit/Deal</p>
                        <p className="text-2xl font-bold font-mono text-blue-600">
                          {formatCurrency(ytdData.summary.avg_profit_per_deal || 0)}
                        </p>
                      </CardContent>
                    </Card>
                  </div>
                  
                  {/* Active Portfolio */}
                  {ytdData.active_portfolio && (
                    <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                      <Card className="rounded-sm border">
                        <CardContent className="p-4">
                          <p className="text-xs uppercase text-muted-foreground">Active Projects</p>
                          <p className="text-xl font-bold">{ytdData.active_portfolio.active_count}</p>
                        </CardContent>
                      </Card>
                      <Card className="rounded-sm border">
                        <CardContent className="p-4">
                          <p className="text-xs uppercase text-muted-foreground">Capital Deployed</p>
                          <p className="text-xl font-bold font-mono">
                            {formatCurrency(ytdData.active_portfolio.total_invested)}
                          </p>
                        </CardContent>
                      </Card>
                      <Card className="rounded-sm bg-purple-50 border-purple-200">
                        <CardContent className="p-4">
                          <p className="text-xs uppercase text-muted-foreground">Notes Receivable</p>
                          <p className="text-xl font-bold font-mono">
                            {formatCurrency(ytdData.active_portfolio.notes_receivable || 0)}
                          </p>
                        </CardContent>
                      </Card>
                    </div>
                  )}
                </div>
              ) : (
                <div className="py-12 text-center text-muted-foreground">
                  Select a year and click refresh to load the summary.
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Cash Flow Projection Tab */}
        <TabsContent value="cashflow" className="space-y-4">
          <Card className="rounded-sm border">
            <CardHeader className="pb-2">
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle className="text-base">Cash Flow Projection</CardTitle>
                  <CardDescription>Expected payments from notes receivable</CardDescription>
                </div>
                <div className="flex items-center gap-2">
                  <Select value={cashFlowMonths.toString()} onValueChange={(v) => setCashFlowMonths(parseInt(v))}>
                    <SelectTrigger className="w-32 rounded-sm">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="6">6 months</SelectItem>
                      <SelectItem value="12">12 months</SelectItem>
                      <SelectItem value="24">24 months</SelectItem>
                      <SelectItem value="36">36 months</SelectItem>
                    </SelectContent>
                  </Select>
                  <Button onClick={loadCashFlow} disabled={loading} size="sm" className="rounded-sm">
                    {loading ? <RefreshCw className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />}
                  </Button>
                  {cashFlowData && (
                    <Button onClick={exportCashFlowToPDF} variant="outline" size="sm" className="rounded-sm">
                      <Download className="w-4 h-4 mr-1" /> Export PDF
                    </Button>
                  )}
                </div>
              </div>
            </CardHeader>
            <CardContent>
              {loading ? (
                <div className="py-12 text-center text-muted-foreground">Loading projection...</div>
              ) : cashFlowData ? (
                <div className="space-y-6">
                  {/* Summary Cards */}
                  <div className="grid grid-cols-3 gap-4">
                    <Card className="rounded-sm border">
                      <CardContent className="p-4">
                        <p className="text-xs uppercase text-muted-foreground">Total Notes Receivable</p>
                        <p className="text-2xl font-bold font-mono text-emerald-600">
                          {formatCurrency(cashFlowData.summary.total_notes_receivable)}
                        </p>
                      </CardContent>
                    </Card>
                    <Card className="rounded-sm bg-blue-50 border-blue-200">
                      <CardContent className="p-4">
                        <p className="text-xs uppercase text-muted-foreground">Expected in {cashFlowMonths} mo</p>
                        <p className="text-2xl font-bold font-mono">
                          {formatCurrency(cashFlowData.summary.total_expected_payments)}
                        </p>
                      </CardContent>
                    </Card>
                    <Card className="rounded-sm bg-purple-50 border-purple-200">
                      <CardContent className="p-4">
                        <p className="text-xs uppercase text-muted-foreground">Active Notes</p>
                        <p className="text-2xl font-bold font-mono">
                          {cashFlowData.summary.active_notes}
                        </p>
                      </CardContent>
                    </Card>
                  </div>
                  
                  {/* Monthly Projections */}
                  {cashFlowData.monthly_projections?.length > 0 && (
                    <div>
                      <h3 className="font-semibold mb-3">Monthly Projections</h3>
                      <div className="overflow-x-auto">
                        <table className="w-full text-sm">
                          <thead>
                            <tr className="border-b text-left">
                              <th className="pb-2 font-medium text-muted-foreground">Month</th>
                              <th className="pb-2 font-medium text-muted-foreground text-right">Expected Payments</th>
                              <th className="pb-2 font-medium text-muted-foreground text-right">Notes Due</th>
                            </tr>
                          </thead>
                          <tbody>
                            {cashFlowData.monthly_projections.map((month, idx) => (
                              <tr key={idx} className="border-b">
                                <td className="py-2 font-medium">{month.month}</td>
                                <td className="py-2 text-right font-mono text-emerald-600">
                                  {formatCurrency(month.expected_payments)}
                                </td>
                                <td className="py-2 text-right">{month.notes_count}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  )}
                  
                  {cashFlowData.monthly_projections?.length === 0 && (
                    <div className="py-8 text-center text-muted-foreground">
                      No notes receivable with scheduled payments.
                    </div>
                  )}
                </div>
              ) : (
                <div className="py-12 text-center text-muted-foreground">
                  Select a time range and click refresh to load the projection.
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default FinancialReports;
